import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  datetimeLocalToIso,
  toDatetimeLocalInput,
} from "@/core/utils/func/format-date";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { SimulateWalletTransactionFailedRequest } from "@/features/notification/api/dto";
import { useSimulateTransactionFailed } from "@/features/notification/api/use-simulate-transaction-failed";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createSimulateTransactionFailedSchema,
  type SimulateTransactionFailedFormValues,
} from "@/features/notification/schemas/simulate-event.schema";
import { PREFERRED_LANGUAGES } from "@/features/shared/api/enums";
import {
  usePartySelectOptions,
  useTransactionReferenceSelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: SimulateTransactionFailedFormValues = {
  eventId: "",
  transactionReference: "",
  partyId: "",
  recipientAddress: "",
  preferredLanguage: "FR",
  failureReason: "",
  occurredAt: "",
};

const freshValues = (): SimulateTransactionFailedFormValues => ({
  ...EMPTY_VALUES,
  eventId: crypto.randomUUID(),
  occurredAt: toDatetimeLocalInput(),
});

const toPayload = (
  values: SimulateTransactionFailedFormValues
): SimulateWalletTransactionFailedRequest => ({
  eventId: values.eventId.trim(),
  transactionReference: values.transactionReference.trim(),
  partyId: values.partyId.trim(),
  recipientAddress: values.recipientAddress.trim(),
  preferredLanguage: values.preferredLanguage,
  failureReason: values.failureReason.trim() || undefined,
  occurredAt: datetimeLocalToIso(values.occurredAt.trim()),
});

/**
 * Dialog `simulate-wallet-transaction-failed` — notification d'échec.
 * `partyId` / référence transaction : selects searchables. `eventId` : UUID client.
 */
export function SimulateTransactionFailedDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tNotif } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createSimulateTransactionFailedSchema(), [i18n.language]);

  const simulate = useSimulateTransactionFailed();
  const pending = simulate.isPending;
  const [preview, setPreview] =
    useState<SimulateWalletTransactionFailedRequest | null>(null);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { options: transactionOptions, isLoading: transactionsLoading } =
    useTransactionReferenceSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  useEffect(() => {
    if (!open) {
      setPreview(null);
      form.reset(EMPTY_VALUES);
      return;
    }
    form.reset(freshValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé à l'ouverture.
  }, [open]);

  const fieldError =
    (name: keyof SimulateTransactionFailedFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const handleConfirm = () => {
    if (!preview) return;
    simulate.mutate(preview, {
      onSuccess: () => {
        notifSilence(tNotif("notifications.events.feedback.sent"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {tNotif("notifications.events.transactionFailed.title")}
            </DialogTitle>
            <DialogDescription>
              {tNotif("notifications.events.warning")}
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="eventId" validators={{ onChange: fieldError("eventId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tf-eventId">
                    {tNotif("notifications.events.common.eventId")}
                  </Label>
                  <Input
                    id="tf-eventId"
                    value={field.state.value}
                    placeholder={tNotif("notifications.events.common.eventIdPlaceholder")}
                    className="notranslate font-mono"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="transactionReference"
              validators={{ onChange: fieldError("transactionReference") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tf-txref">
                    {tNotif(
                      "notifications.events.transactionFailed.transactionReference"
                    )}
                  </Label>
                  <EntitySearchableSelect
                    id="tf-txref"
                    options={transactionOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif(
                      "notifications.events.transactionFailed.transactionReferencePlaceholder"
                    )}
                    isLoading={transactionsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tf-partyId">
                    {tNotif("notifications.events.common.partyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="tf-partyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif("notifications.events.common.partyIdPlaceholder")}
                    isLoading={partiesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="recipientAddress"
              validators={{ onChange: fieldError("recipientAddress") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tf-recipient">
                    {tNotif("notifications.events.common.recipientAddress")}
                  </Label>
                  <Input
                    id="tf-recipient"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.common.recipientAddressPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="preferredLanguage"
              validators={{ onChange: fieldError("preferredLanguage") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tNotif("notifications.events.common.preferredLanguage")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as SimulateTransactionFailedFormValues["preferredLanguage"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_LANGUAGES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="failureReason"
              validators={{ onChange: fieldError("failureReason") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tf-reason">
                    {tNotif("notifications.events.transactionFailed.failureReason")}
                  </Label>
                  <Textarea
                    id="tf-reason"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.transactionFailed.failureReasonPlaceholder"
                    )}
                    rows={3}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="occurredAt"
              validators={{ onChange: fieldError("occurredAt") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tf-occurredAt">
                    {tNotif("notifications.events.transactionFailed.occurredAt")}
                  </Label>
                  <Input
                    id="tf-occurredAt"
                    type="datetime-local"
                    value={field.state.value}
                    className="notranslate"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {tNotif("notifications.events.confirm.action")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={tNotif("notifications.events.confirm.title")}
        description={tNotif("notifications.events.confirm.description")}
        t={tNotif}
        confirmLabel={tNotif("notifications.events.confirm.action")}
        pending={pending}
        onConfirm={handleConfirm}
      />
    </>
  );
}
